import * as THREE from 'three';
import {
  createFeatureFaceMaterial,
  FeatureFaceMaterialState,
  LineMaterialFactory,
  LineMaterialState,
  disposeOwnedMaterial,
} from '../../material';
import {
  isFeatureFace,
  isFeatureLine,
  isFeaturePoint,
} from '../../mesh/feature';
import type { FeatureFace, FeatureLine, FeaturePoint } from '../../mesh/feature';

/**
 * 重置导入拓扑代理的普通交互视觉状态。
 *
 * 设计说明：
 * 1. Mate PropertyManager 吸收 FeatureFace / FeatureLine / FeaturePoint 后，高亮应交给命令态 overlay；
 * 2. 普通 selected 材质如果继续留在代理对象本体上，预览求解移动组件时会出现“蓝色面在旧位置、实体在新位置”的错位；
 * 3. 因此这里只负责把代理本体恢复到默认交互视觉，不创建或修改 Mate 定义，也不处理任何 UI 状态。
 *
 * @param object 需要归还普通交互视觉的场景对象。
 * @returns 对象属于可重置拓扑代理时返回 true，否则返回 false。
 */
export function resetImportedTopologyProxyInteractionVisual(
  object: THREE.Object3D | null | undefined,
): boolean {
  if (isFeatureFace(object)) {
    resetFeatureFaceInteractionVisual(object);
    return true;
  }

  if (isFeatureLine(object)) {
    resetFeatureLineInteractionVisual(object);
    return true;
  }

  if (isFeaturePoint(object)) {
    resetFeaturePointInteractionVisual(object);
    return true;
  }

  return false;
}

/**
 * 重置面代理的普通交互材质。
 *
 * @param face 需要重置的 FeatureFace。
 */
function resetFeatureFaceInteractionVisual(face: FeatureFace): void {
  const previousMaterial = face.material;
  face.material = createFeatureFaceMaterial(FeatureFaceMaterialState.Default);
  face.visible = true;
  /**
   * 普通交互状态机可能复用共享材质，释放时必须尊重 ownership 标记。
   * 默认材质已重新挂载，旧 selected/hover 材质不应继续占用 GPU 资源。
   */
  disposeOwnedMaterial(previousMaterial);
}

/**
 * 重置边线代理的普通交互材质。
 *
 * @param line 需要重置的 FeatureLine。
 */
function resetFeatureLineInteractionVisual(line: FeatureLine): void {
  // 命令接管后仍复用线自身的默认槽，旧 Hover/Selected 绑定在代理结束时统一释放。
  line.material = line.getInteractionMaterial('default', LineMaterialFactory.getFeatureLineMaterialByState(LineMaterialState.Default));
  line.visible = true;
}

/**
 * 重置点代理的普通交互材质。
 *
 * @param point 需要重置的 FeaturePoint。
 */
function resetFeaturePointInteractionVisual(point: FeaturePoint): void {
  // 与普通状态机共享点自身的有限状态缓存，禁止克隆节点图或提前释放另一状态。
  point.material = point.getInteractionMaterial('default');
  /**
   * FeaturePoint 默认态本身不可见；命令态高亮会通过 overlay 重新绘制需要展示的点。
   */
  point.visible = false;
}
